// src/modules/operaciones/inventarios/components/TablaAjustes.js
//
// Tabla principal de ajustes (FilaAjuste) y su skeleton de carga.
import React from "react"; // Importa React para usar JSX
import { fmtFecha } from "../../../../utils/format"; // Fechas compartidas
import { COLUMNAS, txt, lineasDe } from "../utils/constantes"; // Columnas y helpers
import { tieneAlerta } from "../utils/reglasPrecio"; // Detecta alertas por ajuste
import Badge from "./Badge"; // Etiqueta pequeña de color

const ANCHOS_SKELETON = ["w-12", "w-20", "w-16", "flex-1", "w-24", "w-20", "w-24"]; // Anchos de las celdas fantasma por columna

// Placeholder animado de la tabla mientras carga
export const SkeletonTabla = ({ tema }) => (
  // Contenedor marcado como ocupado para accesibilidad
  <div aria-busy="true" className={`rounded-2xl border ${tema.bordeSuave} overflow-hidden`}>
    {/* Encabezado fantasma */}
    <div className={`${tema.thead} px-4 py-3 border-b ${tema.bordeInterno}`}>
      <div className="flex gap-4">
        {/* Una barra por cada columna de datos */}
        {COLUMNAS.map((c) => (
          <div key={c.key} className="h-3 bg-gray-300/50 rounded w-16 animate-pulse" />
        ))}
        {/* Barras para las columnas Precio y Acciones */}
        <div className="h-3 bg-gray-300/50 rounded w-16 animate-pulse" />
        <div className="h-3 bg-gray-300/50 rounded w-24 animate-pulse" />
      </div>
    </div>
    {/* 6 filas fantasma con retraso escalonado de animación */}
    {Array.from({ length: 6 }).map((_, i) => (
      <div
        key={i}
        className={`px-4 py-3 border-b ${tema.bordeFila} flex gap-4 animate-pulse`}
        style={{ animationDelay: `${i * 80}ms` }}
      >
        {/* Celdas fantasma con anchos variados */}
        {ANCHOS_SKELETON.map((w, j) => (
          <div key={j} className={`h-3 bg-gray-300/40 rounded ${w}`} />
        ))}
      </div>
    ))}
  </div>
);

// Fila de la tabla de ajustes con estado visual, botones Ajustar/Exportar/Ver
const FilaAjuste = ({ a, alerta, tema, deshabilitado, listo, ajustando, visto, permanente, onVer, onAjustar, onExportar }) => {
  const anulado = txt(a.AJUS_ANULADO) === "S"; // true si el ajuste está anulado
  // Color del borde izquierdo según prioridad: permanente > visto > alerta > anulado > activo
  const bordeColor = permanente
    ? "border-l-emerald-600"
    : visto
    ? "border-l-emerald-300"
    : alerta
    ? "border-l-red-500"
    : anulado
    ? "border-l-gray-400"
    : "border-l-emerald-500";
  // Crea un handler que evita abrir la fila al pulsar un botón interno
  const alPulsar = (fn) => (e) => {
    e.stopPropagation(); // Evita que el click llegue al onClick de la fila
    fn(a); // Ejecuta la acción con este ajuste
  };
  return (
    // Fila clicable y enfocable; Enter la abre
    <tr
      onClick={() => onVer(a)}
      onKeyDown={(e) => e.target === e.currentTarget && e.key === "Enter" && onVer(a)}
      tabIndex={0}
      className={`border-b border-l-4 ${bordeColor} ${tema.bordeFila} ${tema.hoverFila} hover:shadow-md hover:-translate-y-0.5 cursor-pointer transition-all duration-200 ${visto ? "opacity-70" : ""}`}
    >
      {/* Celda ID con icono de visto/revisado */}
      <td className="px-4 py-3 text-center font-mono">
        <span className="inline-flex items-center gap-1.5">
          {visto && (
            <span title={permanente ? "Revisado (permanente)" : "Visto en esta sesión"} aria-label={permanente ? "Revisado" : "Visto en esta sesión"}>
              {permanente ? "✅" : "👁️"}
            </span>
          )}
          {a.AJUS_ID}
        </span>
      </td>
      {/* Celda fecha formateada */}
      <td className="px-4 py-3 text-center whitespace-nowrap">{fmtFecha(a.AJUS_FECHA)}</td>
      {/* Celda bodega como badge */}
      <td className="px-4 py-3 text-center">
        <Badge tema={tema} tono="gray" cuadrado>
          {txt(a.BODE_COD) || "—"}
        </Badge>
      </td>
      {/* Celda concepto truncada con tooltip */}
      <td className="px-4 py-3 text-left max-w-[320px]">
        <span className="block truncate" title={txt(a.AJUS_CONC)}>
          {txt(a.AJUS_CONC) || "—"}
        </span>
      </td>
      {/* Celda estado: badge de alerta (si aplica) y Activo/Anulado */}
      <td className="px-4 py-3 text-center">
        <div className="flex items-center justify-center gap-1.5 flex-wrap">
          {alerta && (
            <Badge tema={tema} tono="red" pulse>
              ⚠️ Alerta
            </Badge>
          )}
          <Badge tema={tema} tono={anulado ? "red" : "green"}>
            {anulado ? "Anulado" : "Activo"}
          </Badge>
        </div>
      </td>
      {/* Celda con el botón Ajustar (calcula precios de este ajuste) */}
      <td className="px-4 py-3 text-center">
        <button
          onClick={alPulsar(onAjustar)}
          disabled={deshabilitado}
          className={`px-3 py-1.5 rounded-xl text-white text-[11px] font-bold shadow-sm cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed transition-all ${
            listo ? "bg-emerald-600" : "bg-emerald-500 hover:bg-emerald-600"
          }`}
        >
          {listo ? "✓ Listo" : ajustando ? "⏳ Ajustando..." : "💰 Ajustar"}
        </button>
      </td>
      {/* Celda con botones Exportar y Ver */}
      <td className="px-4 py-3 text-center">
        <div className="flex items-center justify-center gap-1.5">
          {/* Botón exportar este ajuste a Excel */}
          <button
            onClick={alPulsar(onExportar)}
            className="px-3 py-1.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-[11px] font-bold shadow-sm cursor-pointer whitespace-nowrap transition-all"
          >
            📊
          </button>
          {/* Botón ver detalle; cambia de color si ya fue visto */}
          <button
            onClick={alPulsar(onVer)}
            className={`px-3 py-1.5 rounded-xl text-[11px] font-bold border shadow-sm cursor-pointer whitespace-nowrap transition-all ${
              visto ? "bg-emerald-500/15 text-emerald-800 border-emerald-400 hover:bg-emerald-500/25" : `${tema.btnBg} ${tema.btnTexto} ${tema.btnBorde}`
            }`}
          >
            {permanente ? "✅ Ver" : visto ? "👁️ Ver" : "Ver"}
          </button>
        </div>
      </td>
    </tr>
  );
};

// Tabla de ajustes de la página actual (extraída del render de Invent.js sin cambios de markup)
export default function TablaAjustes({ tema, lista, visiblesRevision, detallePorAjuste, precios, px, loading, estaVisto, esPermanente, abrirAjuste, exportarAjuste }) {
  // Contenedor con scroll de la tabla de ajustes
  return (
      <div className={`max-h-[50vh] overflow-auto rounded-2xl border ${tema.bordeSuave}`}>
        <table className={`w-full text-left text-[11px] ${tema.textoNormal}`}>
          {/* Encabezado fijo con columnas ordenables */}
          <thead className={`${tema.thead} sticky top-0 border-b ${tema.bordeInterno} shadow-sm z-10 backdrop-blur`}>
            <tr>
              {/* Columnas de datos; clic cambia el orden */}
              {COLUMNAS.map((c) => (
                <th
                  key={c.key}
                  onClick={() => lista.cambiarOrden(c.key)}
                  aria-sort={lista.orden.key === c.key ? (lista.orden.dir === "asc" ? "ascending" : "descending") : "none"}
                  className={`px-4 py-3 font-bold cursor-pointer select-none whitespace-nowrap ${c.align}`}
                >
                  {c.label}
                  {/* Flecha que indica la dirección del orden activo */}
                  <span className={`ml-1 ${tema.textoMuted}`}>
                    {lista.orden.key === c.key ? (lista.orden.dir === "asc" ? "▲" : "▼") : ""}
                  </span>
                </th>
              ))}
              {/* Columnas fijas de botones */}
              <th className="px-4 py-3 font-bold text-center whitespace-nowrap">Precio</th>
              <th className="px-4 py-3 font-bold text-center whitespace-nowrap">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {/* Una fila por ajuste de la página actual */}
            {visiblesRevision.map((a) => (
              <FilaAjuste
                key={a.AJUS_ID}
                a={a}
                tema={tema}
                alerta={tieneAlerta(lineasDe(detallePorAjuste, a), precios)}
                deshabilitado={px.calculando || loading}
                listo={px.checks[a.AJUS_ID]}
                ajustando={px.ajustandoId === a.AJUS_ID}
                visto={estaVisto(a.AJUS_ID)}
                permanente={esPermanente(a.AJUS_ID)}
                onVer={abrirAjuste}
                onAjustar={px.ajustar}
                onExportar={exportarAjuste}
              />
            ))}
          </tbody>
        </table>
      </div>
  );
}